// ============================================================
// GitBranching.jsx
//
// A branded, responsive course content page for the
// "Git Branching & Merging" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Parallel development logic, branch switching,
//           merge strategies, and conflict resolution.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AccountTreeIcon        from "@mui/icons-material/AccountTree";
import CallSplitIcon          from "@mui/icons-material/CallSplit";
import MergeTypeIcon          from "@mui/icons-material/MergeType";
import PriorityHighIcon       from "@mui/icons-material/PriorityHigh";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import SwapHorizIcon          from "@mui/icons-material/SwapHoriz";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitBranching() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Parallel Development"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Branching & <span>Merging Logic</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Work on new features or bug fixes without touching the stable codebase. Git branching allows for total isolation, while merging brings that innovation back into the main line of history.
                    </Typography>
                </Box>

                {/* 1. BRANCHING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CallSplitIcon />} label="1. Creating & Navigating Branches" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        A branch is essentially a pointer to a specific commit. Creating a new one lets you diverge from the <code>main</code> timeline.
                    </Typography>
                    <CodeBlock
                        title="Branch Commands"
                        code={`# Create a new branch\ngit branch feature-login\n\n# Switch to that branch\ngit switch feature-login\n\n# Shortcut: Create and switch in one command\ngit checkout -b feature-payment`}
                    />
                </Box>

                {/* 2. MERGING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<MergeTypeIcon />} label="2. Merging Changes" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        When your feature is complete, you bring it back home. Always switch to the <strong>target</strong> branch (usually <code>main</code>) before initiating the merge.
                    </Typography>
                    <CodeBlock
                        title="The Merge Process"
                        code={`# Switch to the receiving branch\ngit switch main\n\n# Pull in the feature changes\ngit merge feature-login`}
                    />
                </Box>

                {/* 3. CONFLICTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PriorityHighIcon />} label="3. Resolving Merge Conflicts" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Conflicts happen when Git can't decide which change to keep (e.g., two people edited the same line). Git pauses the merge and marks the file for manual resolution.
                    </Typography>
                    <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 3 }}>
                        <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>Run <strong>git status</strong> to find conflicted files.</Typography></Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>Open the file and choose between &lt;&lt;&lt;HEAD and === changes.</Typography></Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>Remove markers, <strong>git add</strong> the file, and <strong>git commit</strong> to finish.</Typography></Box>
                        </Stack>
                    </Paper>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BRANCHING HIGHLIGHTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Branching Strategy Tips" />
                    <Grid container spacing={1.5}>
                        {[
                            "Keep 'main' or 'master' deployable at all times.",
                            "Use descriptive branch names (e.g., fix/header-alignment).",
                            "Delete feature branches locally and remotely after a successful merge.",
                            "Merge 'main' into your feature branch frequently to avoid large conflicts.",
                            "Use 'git branch -a' to see all local and remote branches.",
                            "Never commit code directly to 'main' in a collaborative team."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Using <code>git switch -c branch-name</code> is the modern, clearer way to create and switch branches, replacing the older <code>git checkout -b</code>.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
